import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Container } from './ui/Container'
import { 
  Heart, 
  Menu, 
  X, 
  ChevronDown, 
  Microscope, 
  Brain, 
  Apple, 
  Pill, 
  Hotel, 
  FlaskConical, 
  BarChart3,
  Sparkles,
  Globe
} from 'lucide-react'
import ThemeToggle from './ThemeToggle'
import { motion, AnimatePresence } from 'framer-motion'

interface NavItem {
  label: string
  path?: string
  children?: {
    label: string
    path: string
    icon: React.ElementType
    description: string
  }[]
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const navItems: NavItem[] = [
    {
      label: 'Platforms',
      children: [
        { label: 'Lab OS', path: '/features', icon: Microscope, description: 'Standing monthly clinical-grade panels.' },
        { label: 'AI OS', path: '/', icon: Brain, description: 'Synthesizing data into actionable scores.' },
        { label: 'Food OS', path: '/meal-planning', icon: Apple, description: 'Bio-synced nutrition & automated meals.' },
        { label: 'Supplement OS', path: '/meal-planning', icon: Pill, description: 'Custom formulations mapped to labs.' },
        { label: 'AI Lab', path: '/ai-lab', icon: Sparkles, description: 'Generate health graphics & educational videos.' },
      ]
    },
    {
      label: 'Ecosystem',
      children: [
        { label: 'Lab Network', path: '/partners/labs', icon: FlaskConical, description: 'Diagnostic kit fulfillment and clinical data node.' },
        { label: 'Meal Fulfillment', path: '/partners/meals', icon: Apple, description: 'HelloFresh-style recipe packaging and shipping.' },
        { label: 'Restaurant Hub', path: '/partners/restaurants', icon: Hotel, description: 'On-demand local meal prep and elite delivery.' },
      ]
    },
    {
      label: 'Investors',
      children: [
        { label: 'Pitch Deck', path: '/pitch-deck', icon: BarChart3, description: 'The OS for Human Biology memorandum.' },
        { label: 'Market Vision', path: '/pitch-deck', icon: Globe, description: 'Long-term longevity category leadership.' },
      ]
    },
    { label: 'Pricing', path: '/pricing' },
    { label: 'About', path: '/about' },
  ]

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const handleSignIn = () => {
    setIsMenuOpen(false)
    navigate('/auth/signin')
  }

  const handleGetStarted = () => {
    setIsMenuOpen(false)
    navigate('/pricing')
  }

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-theme-surface/80 backdrop-blur-md border-b border-theme-border theme-transition" ref={dropdownRef}>
      <Container size="full">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" onClick={() => setActiveDropdown(null)}>
            <div className="w-10 h-10 bg-gradient-to-br from-theme-primary to-theme-secondary rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3 shadow-lg shadow-theme-primary/20">
              <Heart className="w-5 h-5 text-theme-text-inverse" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-xl font-black text-theme-text tracking-tight">BioTune</span>
              <span className="text-[10px] text-theme-secondary uppercase tracking-[0.1em] font-bold">
                Level 10 Longevity
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <div key={item.label} className="relative">
                {item.children ? (
                  <button
                    onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                    className={`flex items-center space-x-1 px-4 py-2 text-sm font-bold transition-all rounded-full hover:bg-theme-primary/5 ${
                      activeDropdown === item.label ? 'text-theme-primary bg-theme-primary/10' : 'text-theme-secondary hover:text-theme-primary'
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === item.label ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={item.path!}
                    onClick={() => setActiveDropdown(null)}
                    className={`px-4 py-2 text-sm font-bold transition-all rounded-full hover:bg-theme-primary/5 ${
                      isActive(item.path!) ? 'text-theme-primary bg-theme-primary/5' : 'text-theme-secondary hover:text-theme-primary'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}

                {/* Desktop Dropdown */}
                <AnimatePresence>
                  {activeDropdown === item.label && item.children && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute top-full left-0 mt-2 w-72 bg-theme-surface border border-theme-border rounded-2xl shadow-2xl p-4 overflow-hidden"
                    >
                      <div className="space-y-1">
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setActiveDropdown(null)}
                            className="flex items-start space-x-4 p-3 rounded-xl hover:bg-theme-primary/5 transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-theme-primary/10 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                              <child.icon className="w-5 h-5 text-theme-primary" />
                            </div>
                            <div>
                              <div className="text-sm font-bold text-theme-text group-hover:text-theme-primary transition-colors">{child.label}</div>
                              <div className="text-[11px] text-theme-secondary leading-tight mt-0.5">{child.description}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-theme-border">
                        <div className="flex items-center space-x-2 px-3 py-1 bg-theme-primary/5 rounded-lg text-[10px] font-bold text-theme-primary uppercase tracking-wider">
                          <Sparkles className="w-3 h-3" />
                          <span>Platform Premium Feature</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <ThemeToggle />
            <Link
              to="/dashboard"
              className="px-5 py-2.5 text-sm font-bold text-theme-primary hover:text-theme-primary/80 transition-colors"
            >
              My Dashboard
            </Link>
            <button
              onClick={handleGetStarted}
              className="px-6 py-2.5 text-sm font-bold bg-theme-primary text-theme-text-inverse rounded-full hover:shadow-lg hover:shadow-theme-primary/30 transition-all hover:-translate-y-0.5 active:translate-y-0"
            >
              Upgrade Plan
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-4">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2.5 rounded-xl bg-theme-surface border border-theme-border text-theme-secondary hover:text-theme-primary transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-theme-border overflow-hidden"
            >
              <nav className="flex flex-col space-y-2 py-6">
                {navItems.map((item) => (
                  <div key={item.label} className="space-y-1">
                    {item.children ? (
                      <>
                        <div className="px-4 py-2 text-xs font-black text-theme-secondary uppercase tracking-[0.2em]">{item.label}</div>
                        {item.children.map((child) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            className="flex items-center space-x-4 px-4 py-3 rounded-xl hover:bg-theme-primary/5 transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-theme-primary/10 flex items-center justify-center">
                              <child.icon className="w-4 h-4 text-theme-primary" />
                            </div>
                            <span className="text-sm font-bold text-theme-text">{child.label}</span>
                          </Link>
                        ))}
                      </>
                    ) : (
                      <Link
                        to={item.path!}
                        className={`block px-4 py-3 text-sm font-bold rounded-xl transition-colors ${
                          isActive(item.path!) ? 'text-theme-primary bg-theme-primary/10' : 'text-theme-text hover:bg-theme-primary/5'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
                
                <div className="flex flex-col space-y-3 pt-6 border-t border-theme-border">
                  <button
                    onClick={handleSignIn}
                    className="w-full px-6 py-3 text-sm font-bold border border-theme-border rounded-xl text-theme-primary hover:bg-theme-surface transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleGetStarted}
                    className="w-full px-6 py-3 text-sm font-bold bg-theme-primary text-theme-text-inverse rounded-xl shadow-lg shadow-theme-primary/20 transition-all"
                  >
                    Get Started
                  </button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>
    </header>
  )
}

export default Header